import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Sparkles,
  Clock,
  Shield,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EstimateWidget from '../components/EstimateWidget';
import { Button } from '../components/ui/button';
import SEO from '../seo/SEO';
import { faqPageSchema } from '../seo/schemas';
import { SITE_URL } from '../seo/siteConfig';
import {
  COMPETITORS_BY_SLUG,
  COMPETITORS,
} from '../data/competitors';

// BreadcrumbList for /compare/:slug — supports rich breadcrumb display in SERPs.
function breadcrumbSchema(competitor) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Compare', item: `${SITE_URL}/compare` },
      {
        '@type': 'ListItem',
        position: 3,
        name: `vs ${competitor.name}`,
        item: `${SITE_URL}/compare/${competitor.slug}`,
      },
    ],
  };
}

// Service schema specific to this comparison
function compareServiceSchema(competitor) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Third-party logistics (3PL) fulfillment',
    name: `Shipmaster vs ${competitor.name}: 3PL Comparison`,
    provider: {
      '@type': 'Organization',
      name: 'Shipmaster Fulfillment',
      url: SITE_URL,
    },
    description: competitor.blurb,
    url: `${SITE_URL}/compare/${competitor.slug}`,
  };
}

export default function ComparePage() {
  const { slug } = useParams();
  const competitor = COMPETITORS_BY_SLUG[slug];
  if (!competitor) return <Navigate to="/compare" replace />;

  const others = COMPETITORS.filter((c) => c.slug !== slug);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title={`Shipmaster vs ${competitor.name} — 3PL Comparison`}
        description={`Considering ${competitor.name} for ecommerce fulfillment? See how Shipmaster compares on pricing, minimums, onboarding, and operational consistency. Honest side-by-side analysis.`}
        keywords={`${competitor.name} alternative, Shipmaster vs ${competitor.name}, ${competitor.name} vs Shipmaster, ${competitor.name} review, switch from ${competitor.name}, ${competitor.name} pricing, 3PL alternative`}
        schemas={[
          breadcrumbSchema(competitor),
          compareServiceSchema(competitor),
          faqPageSchema(
            competitor.switchFaqs.map((f) => ({ question: f.q, answer: f.a })),
          ),
        ]}
      />
      <Header />
      <main>
        {/* Hero */}
        <section
          className="relative overflow-hidden text-white"
          style={{
            background:
              'linear-gradient(135deg, #1e1b4b 0%, #312e81 35%, #4f46e5 100%)',
          }}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <Link
              to="/compare"
              className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-200 hover:text-white transition-colors"
              data-testid="compare-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> All comparisons
            </Link>
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-xs font-bold uppercase tracking-widest">
              <Sparkles className="w-3.5 h-3.5" /> 3PL Comparison
            </div>
            <h1 className="mt-4 text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              Shipmaster Fulfillment vs {competitor.name}
            </h1>
            <p className="mt-3 max-w-2xl text-lg text-indigo-200 font-semibold">
              {competitor.tagline}
            </p>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-indigo-100 leading-relaxed">
              {competitor.blurb}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                size="lg"
                className="bg-white text-indigo-700 hover:bg-indigo-50 rounded-full font-bold h-12 px-7 shadow-lg"
                data-testid="compare-hero-cta"
              >
                <Link to="/#contact">Get a Free Estimate</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-white/40 text-white hover:bg-white/10 rounded-full font-bold h-12 px-7"
              >
                <Link to="/zones">View Zone Map</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Side-by-side table */}
        <section className="py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
              Side by side
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
              How we stack up against {competitor.name}
            </h2>
            <p className="mt-3 max-w-2xl text-slate-600 leading-relaxed">
              Honest comparison based on each company&apos;s public pricing and
              policies as of February 2026. Verify current rates with{' '}
              {competitor.name} directly before deciding.
            </p>

            <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-5 py-4 text-xs font-bold uppercase tracking-widest text-slate-500 w-[40%]">
                      What it costs
                    </th>
                    <th className="text-left px-5 py-4 bg-indigo-50 border-l-4 border-indigo-500">
                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-700">
                        <Sparkles className="w-3.5 h-3.5" /> Shipmaster
                      </div>
                    </th>
                    <th className="text-left px-5 py-4 text-xs font-bold uppercase tracking-widest text-slate-500">
                      {competitor.name}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {competitor.table.map((row) => (
                    <tr
                      key={row.label}
                      className="border-t border-slate-100 hover:bg-slate-50/40"
                    >
                      <td className="px-5 py-4 font-semibold text-slate-700">
                        {row.label}
                      </td>
                      <td className="px-5 py-4 bg-indigo-50/50 border-l-4 border-indigo-500 font-bold text-indigo-900">
                        {row.shipmaster}
                      </td>
                      <td className="px-5 py-4 text-slate-700">{row.them}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Where each wins */}
        <section className="py-14 sm:py-20 bg-slate-50/60 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
              Honest framing
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
              Where each platform wins
            </h2>
            <p className="mt-3 max-w-2xl text-slate-600 leading-relaxed">
              No 3PL is perfect for every brand. Here&apos;s a clear-eyed look
              at what {competitor.name} does well and where Shipmaster
              outperforms.
            </p>

            <div className="mt-10 grid lg:grid-cols-2 gap-6">
              {/* Where they win */}
              <div className="rounded-2xl bg-white border border-slate-200 p-7 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-extrabold tracking-tight">
                    Where {competitor.name} wins
                  </h3>
                </div>
                <ul className="mt-5 space-y-3">
                  {competitor.theyWin.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed"
                    >
                      <AlertCircle
                        className="flex-shrink-0 w-4 h-4 text-amber-600 mt-0.5"
                        strokeWidth={2.5}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Where we win */}
              <div className="rounded-2xl border-2 border-indigo-500 bg-indigo-50/40 p-7 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-extrabold tracking-tight text-indigo-900">
                    Where Shipmaster wins
                  </h3>
                </div>
                <ul className="mt-5 space-y-3">
                  {competitor.weWin.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-sm text-slate-800 leading-relaxed font-medium"
                    >
                      <CheckCircle2
                        className="flex-shrink-0 w-4 h-4 text-indigo-600 mt-0.5"
                        strokeWidth={2.5}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Migration FAQ */}
        <section className="py-14 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
              Migrating from {competitor.name}
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
              Common questions about switching
            </h2>
            <div className="mt-8 space-y-4">
              {competitor.switchFaqs.map((f) => (
                <details
                  key={f.q}
                  className="group rounded-2xl bg-white border border-slate-200 p-5 hover:border-indigo-200 transition-colors"
                >
                  <summary className="flex items-center justify-between cursor-pointer list-none gap-3">
                    <h3 className="font-bold text-base text-slate-900">{f.q}</h3>
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-100 group-open:bg-indigo-100 flex items-center justify-center text-slate-500 group-open:text-indigo-700 transition-colors">
                      <span className="group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                    </span>
                  </summary>
                  <p className="mt-4 text-sm text-slate-700 leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA — embedded estimate widget */}
        <section className="py-14 sm:py-20 bg-slate-50/60 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
                Free instant estimate
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
                See what switching from {competitor.name} could save you
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Drop your store URL and monthly order volume — we&apos;ll show
                you a real-time savings estimate based on your actual lane.
              </p>
            </div>
            <div className="mt-10 max-w-3xl mx-auto">
              <EstimateWidget />
            </div>
          </div>
        </section>

        {/* Cross-links to other comparisons */}
        <section className="py-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
              More comparisons
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
              Comparing other 3PLs?
            </h2>
            <ul className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {others.map((o) => (
                <li key={o.slug}>
                  <Link
                    to={`/compare/${o.slug}`}
                    className="group flex items-center justify-between p-4 rounded-xl bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all"
                    data-testid={`compare-link-${o.slug}`}
                  >
                    <div>
                      <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-700">
                        vs {o.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                        {o.tagline}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
